import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { OUTREACH_CHANNELS } from '../../lib/eventOutreachActions';
import { TONES } from '../../lib/eventJourney';

/**
 * The one form behind every by-hand outreach change (#894): Mark invited on the Attendees tab and on
 * a board card, a board card's stage dropdown or drop, the bulk bar, a Follow-up board drag or its
 * Handed to sales button, and an RSVP set in the Outreach board drawer (`mode="rsvp"`). It asks for
 * the channel (required), an optional note and a follow-up date; the caller saves through
 * `lib/eventOutreachActions`. Cancel changes nothing.
 *
 * The Guests tab (#1013) adds three optional props, none of which changes a caller that omits them:
 * `title` replaces the heading, `effect` (`{tone, text}`) says what the move records before it is saved,
 * and `reasons` (`[{value, label}]`, with `defaultReason`) makes a close ask why; `onSubmit` then also
 * receives `reason`.
 */
export default function EventStageActionDialog({
    open, onClose, onSubmit, mode = 'stage', stageLabel = '', count = 1, finalOutcome = false, busy = false,
    title: titleOverride, effect = null, reasons = null, defaultReason = '',
}) {
    const [channel, setChannel] = useState('');
    const [note, setNote] = useState('');
    const [followUpDate, setFollowUpDate] = useState('');
    const [reason, setReason] = useState('');
    useEffect(() => {
        if (open) { setChannel(''); setNote(''); setFollowUpDate(''); setReason(defaultReason || ''); }
    }, [open, stageLabel, defaultReason]);
    const needsReason = Array.isArray(reasons) && reasons.length > 0;
    const invite = mode === 'invite';
    const rsvp = mode === 'rsvp';
    const who = count > 1 ? `${count} contacts` : '';
    const title = titleOverride || (invite ? (who ? `Mark ${who} invited` : 'Mark invited')
        : rsvp ? `Set RSVP to ${stageLabel}`
            : `Move ${who ? `${who} ` : ''}to ${stageLabel}`);
    const testId = invite ? 'mark-invited-dialog' : rsvp ? 'rsvp-change-dialog' : 'stage-change-dialog';
    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) onClose?.(); }}>
            <DialogContent data-testid={testId}>
                <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
                {effect?.text && (
                    <p data-testid="stage-effect" className="rounded-lg border px-3 py-2 text-xs"
                        style={{ background: (TONES[effect.tone] || TONES.stone).bg, color: (TONES[effect.tone] || TONES.stone).fg, borderColor: (TONES[effect.tone] || TONES.stone).bd }}>
                        {effect.text}
                    </p>
                )}
                {needsReason && (
                    <label className="block text-xs font-medium text-stone-600">Why close?
                        <select aria-label="Reason" value={reason} onChange={event => setReason(event.target.value)} className="mt-1 w-full rounded border border-stone-200 px-2 py-2 text-sm">
                            <option value="">Choose a reason</option>
                            {reasons.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                    </label>
                )}
                <label className="block text-xs font-medium text-stone-600">Channel
                    <select aria-label="Channel" value={channel} onChange={event => setChannel(event.target.value)} className="mt-1 w-full rounded border border-stone-200 px-2 py-2 text-sm">
                        <option value="">How did you reach them?</option>
                        {OUTREACH_CHANNELS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                </label>
                <label className="block text-xs font-medium text-stone-600">Note (optional)
                    <textarea aria-label="Note" value={note} onChange={event => setNote(event.target.value)} className="mt-1 w-full rounded border border-stone-200 px-2 py-2 text-sm" rows={3} />
                </label>
                <label className="block text-xs font-medium text-stone-600">Follow up in
                    <input aria-label="Follow-up date" type="date" value={followUpDate} onChange={event => setFollowUpDate(event.target.value)} className="mt-1 w-full rounded border border-stone-200 px-2 py-2 text-sm" />
                </label>
                {finalOutcome && <p className="text-xs text-amber-700">This ends the outreach for this event. You will be asked whether to close its open follow-ups.</p>}
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={() => onSubmit(needsReason ? { channel, note, followUpDate, reason } : { channel, note, followUpDate })} disabled={busy || !channel || (needsReason && !reason)}>
                        {busy ? 'Saving…' : (invite ? 'Mark invited' : 'Save')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
